"use client";

import React from "react";
import { ResponsiveModal, MODAL_IDS } from "@wtc/ui";
import type { BaseEntity } from "./types";
import { SimpleTableMobileSearchAndFilters } from "./SimpleTableMobileSearchAndFilters";
import { SimpleTableMobileBulkActions } from "./SimpleTableMobileBulkActions";
import { SimpleTableMobileDetailModal } from "./SimpleTableMobileDetailModal";
import { MobileRowActionsSheet } from "./components/SimpleTable/MobileRowActionsSheet";
import { SimpleTableMobilePagination } from "./SimpleTableMobilePagination";
import { SimpleTableMobileSkeleton } from "./SimpleTableMobileSkeleton";
import { EmptyState } from "./components/SimpleTable/EmptyState";
import { MobileViewErrorBoundary } from "./ErrorBoundaries";
import { VirtualizedMobileEntityCards } from "./VirtualizedMobileEntityCards";
import { Skeleton } from "@/components/ui/skeleton";
import { XCircle } from "lucide-react";
import type { SimpleTableMobileViewProps } from "./SimpleTableMobileView/types";
import { useMobileViewState } from "./SimpleTableMobileView/use-mobile-view-state";

/**
 * Generic mobile list host for every CRM entity.
 *
 * Props live in `SimpleTableMobileView/types.ts` and the selection / bulk-action
 * / row-open state in `SimpleTableMobileView/use-mobile-view-state.ts`
 * (module-size lane L17). This module owns the layout and the
 * loading / error / empty branches.
 */
export function SimpleTableMobileView<T extends BaseEntity>(props: SimpleTableMobileViewProps<T>) {
  const {
    config,
    data = [],
    isLoading = false,
    error = null,
    onEdit,
    onView,
    onDelete,
    onInlineEdit,
    onBulkAction,
    mobileHeaderActions,
    loadMore,
    hasNextPage,
    isFetchingNextPage = false,
    currentPage = 1,
    totalPages = 1,
    pageSize = 25,
    totalItems = 0,
    onPageChange = () => {},
    onPageSizeChange = () => {},
    onOpenDetailModal,
    visibleColumnIds,
    hooks,
    onCreate,
  } = props;

  const {
    selectedItems,
    selectedItem,
    actionsItem,
    isActionLoading,
    bulkActionError,
    retryBulkAction,
    dismissBulkActionError,
    handleItemSelect,
    handleBulkAction,
    handleClearSelection,
    handleItemView,
    handleOpenActions,
    handleFallbackModalOpenChange,
    handleActionsModalOpenChange,
    closeActionsSheet,
  } = useMobileViewState<T>({ data, onBulkAction, onOpenDetailModal, onView });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <div className="flex gap-2">
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-8 w-24" />
          </div>
        </div>
        <SimpleTableMobileSkeleton count={5} showActions={true} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <XCircle className="h-12 w-12 mx-auto mb-2" />
          <h3 className="text-lg font-medium">Error loading data</h3>
        </div>
        <p className="text-gray-600 dark:text-gray-400">{error.message}</p>
      </div>
    );
  }

  return (
    <MobileViewErrorBoundary>
      {/* Extra bottom padding on mobile so the last rows + pagination clear the
          fixed floating action button (bottom-right). Reset at md where there's no FAB. */}
      <div className="space-y-4 p-4 pb-24 md:pb-4">
        <SimpleTableMobileSearchAndFilters
          config={config}
          mobileHeaderActions={mobileHeaderActions}
        />

        {data.length > 0 ? (
          <VirtualizedMobileEntityCards
            data={data}
            config={config}
            visibleColumnIds={visibleColumnIds}
            hooks={hooks}
            onEdit={onEdit}
            onDelete={onDelete}
            onInlineEdit={onInlineEdit}
            onView={handleItemView}
            selectedItems={selectedItems}
            onItemSelect={handleItemSelect}
            enableSelection={Boolean(config.features?.bulkActions)}
            onOpenActions={hooks ? handleOpenActions : undefined}
          />
        ) : (
          /* Same empty state as desktop, so the recovery action ("New deal")
             exists on a phone too — this used to be a bare line of text. */
          <EmptyState entityName={config.displayName || config.entity} onCreate={onCreate} />
        )}

        {bulkActionError && selectedItems.length > 0 && (
          /* Sits directly above the fixed bulk bar so the message is where the
             action was, and carries the remediation itself (ERROR_STATES_ACTIONABLE).
             Same stacking context as the bar it belongs to. */
          <div
            role="alert"
            className="fixed bottom-28 left-4 right-4 z-50 flex flex-wrap items-center gap-2 rounded-lg border border-destructive/40 bg-card px-3 py-2 text-sm text-destructive shadow-lg"
          >
            <XCircle className="h-4 w-4 flex-shrink-0" />
            <span className="min-w-0 flex-1">{bulkActionError}</span>
            <button
              type="button"
              onClick={retryBulkAction}
              disabled={Boolean(isActionLoading)}
              className="font-medium underline underline-offset-2 disabled:opacity-50"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={dismissBulkActionError}
              className="text-muted-foreground underline underline-offset-2"
            >
              Dismiss
            </button>
          </div>
        )}

        {config.features?.bulkActions && selectedItems.length > 0 && (
          <SimpleTableMobileBulkActions
            config={config}
            selectedItems={selectedItems}
            onBulkAction={handleBulkAction}
            onClearSelection={handleClearSelection}
            isLoading={isActionLoading}
          />
        )}

        {selectedItem && (
          <ResponsiveModal
            globalModalId={MODAL_IDS.navigation.mobileDetail}
            title={String(selectedItem.name ?? selectedItem.title ?? "Details")}
            onOpenChange={handleFallbackModalOpenChange}
          >
            <SimpleTableMobileDetailModal
              item={selectedItem}
              config={config}
              onEdit={onEdit}
              onDelete={onDelete}
              onInlineEdit={onInlineEdit}
            />
          </ResponsiveModal>
        )}

        {actionsItem && hooks && (
          <ResponsiveModal
            globalModalId={MODAL_IDS.navigation.mobileRowActions}
            title="Actions"
            onOpenChange={handleActionsModalOpenChange}
          >
            <MobileRowActionsSheet
              item={actionsItem}
              actions={config.detailActions ?? []}
              hooks={hooks}
              entityName={config.entity}
              config={config}
              onClose={closeActionsSheet}
            />
          </ResponsiveModal>
        )}

        {loadMore && hasNextPage && (
          <div className="flex justify-center py-4">
            <button
              type="button"
              onClick={loadMore}
              disabled={isFetchingNextPage}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isFetchingNextPage ? "Loading..." : "Load More"}
            </button>
          </div>
        )}

        {/* Pagination is the fallback for "Load More is not on screen". It used
            to be gated on `!loadMore` alone, so an infinite list whose
            `hasNextPage` had gone false showed NEITHER control — no Load More,
            no pager, no page-size control, no way to move. */}
        {!(loadMore && hasNextPage) && (onPageChange || totalItems !== undefined) && (
          <SimpleTableMobilePagination
            config={config}
            currentPage={currentPage || 1}
            totalPages={totalPages || 1}
            pageSize={pageSize || 25}
            totalItems={totalItems || data.length}
            onPageChange={onPageChange || (() => {})}
            onPageSizeChange={onPageSizeChange || (() => {})}
            isLoading={isLoading}
          />
        )}
      </div>
    </MobileViewErrorBoundary>
  );
}
